import {
  useState,
  useRef,
  useEffect,
  lazy,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Icon,
  Button,
  MISC_IMAGE_CHOSEN,
  USER_1_ADULT_1_CHILD,
  TIME_LOCATION_PIN,
  TIME_LOCATION_CALENDAR,
  TIME_LOCATION_BED,
  USER_INTERFACE_CROSS_CANCEL,
} from '@bwoty-web/styleguide';
import { toolPanelOptionIds } from '../../appSettings';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import LazyLoader from '../lazyLoader/lazyLoader';
import { setNeverShowReminder } from '../../redux/actions/resultsActions';
import './toolPanel.scss';

const PaxToolPanelOption = lazy(() => import(/* webpackChunkName: "toolpanel-pax" */ '../paxToolPanelOption/paxToolPanelOption'));
const ClassificationsToolPanelOption = lazy(() => import(/* webpackChunkName: "toolpanel-classifications" */ '../classificationsToolPanelOption/classificationsToolPanelOption'));
const AccommodationToolPanelOption = lazy(() => import(/* webpackChunkName: "toolpanel-accommodation" */ '../accommodationToolPanelOption/accommodationToolPanelOption'));
const TraitsToolPanelOption = lazy(() => import(/* webpackChunkName: "toolpanel-traits" */ '../traitsToolPanelOption/traitsToolPanelOption'));
const CalendarToolPanelOption = lazy(() => import(/* webpackChunkName: "toolpanel-calendar" */ '../calendarToolPanelOption/calendarToolPanelOption'));

const ToolPanel = ({ labels, neverShowReminder, onSetNeverShowReminder }) => {
  const toolPanelRef = useRef();
  const footerPanelRef = useRef();
  const [ showTool, setShowTool ] = useState(false);
  const [ selectedToolPanel, setSelectedToolPanel ] = useState(null);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (showTool) {
      // when toolbar is open, disable the scrolling in the resultlist
      // mother of all side effects, but no better way to do this
      document.querySelector('body').style.overflow = 'hidden';
      document.querySelector('html').style.overflow = 'hidden'; // Fix for iOS
      
      // To not show the reminder if the tool panel has already been opened before the reminder is show. 
      // No need to dispatch this action more than once
      if (!neverShowReminder) {
        onSetNeverShowReminder();
      }
    } else {
      // and restore it when it's closed
      document.querySelector('body').style.overflow = 'auto';
      document.querySelector('html').style.overflow = 'auto';
    }
  }, [showTool]);

  const toolPanelOptions = [
    {
      id: toolPanelOptionIds.ACTIVITIES,
      iconPath: MISC_IMAGE_CHOSEN,
      label: labels.toolPanelClassificationsText,
    },
    {
      id: toolPanelOptionIds.ACCOMMODATION,
      iconPath: TIME_LOCATION_BED,
      label: labels.toolPanelAccommodationText,
    },
    {
      id: toolPanelOptionIds.TRAITS,
      iconPath: TIME_LOCATION_PIN,
      label: labels.toolPanelTraitsText,
    },
    {
      id: toolPanelOptionIds.PAX,
      iconPath: USER_1_ADULT_1_CHILD,
      label: labels.toolPanelPaxText,
    },
    {
      id: toolPanelOptionIds.CALENDAR,
      iconPath: TIME_LOCATION_CALENDAR,
      label: labels.toolPanelCalendarText,
    },
  ];
  
  const handleCloseToolbar = () => {
    setShowTool(false);
    setSelectedToolPanel(null);
  };
 
  useOnClickOutside(toolPanelRef, handleCloseToolbar, [footerPanelRef]);

  const getToolToShow = () => {
    switch (selectedToolPanel) {
      case toolPanelOptionIds.ACTIVITIES:
        return (
          <LazyLoader>
            <ClassificationsToolPanelOption />
          </LazyLoader>
        )
      case toolPanelOptionIds.ACCOMMODATION:
        return (
          <LazyLoader>
            <AccommodationToolPanelOption />
          </LazyLoader>
        )
      case toolPanelOptionIds.TRAITS:
        return (
          <LazyLoader>
            <TraitsToolPanelOption />
          </LazyLoader>
        )
      case toolPanelOptionIds.PAX:
        return (
          <LazyLoader>
            <PaxToolPanelOption />
          </LazyLoader>
        )
      case toolPanelOptionIds.CALENDAR:
        return (
          <LazyLoader>
            <CalendarToolPanelOption />
          </LazyLoader>
        )
      default:
        return null;
    }
  };

  const renderTopClose = () => (
    <button className="vf-tool-panel__close-button" type="button" onClick={handleCloseToolbar}>
      <span className='vf-tool-panel__close-button__label'>{labels.saveAndClose}</span>
      <Icon
        path={USER_INTERFACE_CROSS_CANCEL}
        size={isDesktop ? "s" : "xs"}
      />
    </button>
  );

  const renderBottomClose = () => (
    <Button className='vf-tool-panel__close-button__bottom' onClick={handleCloseToolbar}>
      {labels.saveAndClose}
    </Button>
  )

  const handleToolPanelOptionClick = (id, selected) => {
    if (selected) {
      // if you click the already selected one - toggle the toolpanel
      handleCloseToolbar()
    } else {
      setSelectedToolPanel(id);
      setShowTool(true)
    }
  }

  const renderToolPanelOptions = () => (
    <div ref={footerPanelRef} className='tool-panel-options'>
      {toolPanelOptions.map(({ id, iconPath, label }) => {
        const selected = selectedToolPanel === id;
        return (
          <button
            key={id}
            type='button'
            className={`${selected ? 'vf-tool-panel-option--active' : ''}`}
            onClick={() => { handleToolPanelOptionClick(id, selected) }}
          >
            <Icon className="tool-panel-option__icon" path={iconPath} size={isDesktop ? "m" : "s"} />
            <span className="tool-panel-option__label">{label}</span>
          </button>
        );
      })}
    </div>
  )

  return (
    <>  
      { showTool && (
        <div className='vf-overlay' />
      )}
      <section className='tool-panel-container'>
        {showTool && (
          <div className='tool-panel__content-wrapper'>
            <div className='tool-panel__gradient' />
            <div ref={toolPanelRef} id={selectedToolPanel} className={`tool-panel__content ${showTool ? 'tool-panel__content--visible' : ''}`}>
              { renderTopClose() }
              
              { getToolToShow() }
            
              { renderBottomClose() }
            </div>
          </div>
        )}
        
        { renderToolPanelOptions() }
      </section>
    </>
  )
}

ToolPanel.propTypes = {
  labels: PropTypes.shape({
    saveAndClose: PropTypes.string.isRequired,
    toolPanelClassificationsText: PropTypes.string.isRequired,
    toolPanelAccommodationText: PropTypes.string.isRequired,
    toolPanelTraitsText: PropTypes.string.isRequired,
    toolPanelPaxText: PropTypes.string.isRequired,
    toolPanelCalendarText: PropTypes.string.isRequired,
  }).isRequired,
  neverShowReminder: PropTypes.bool,
  onSetNeverShowReminder: PropTypes.func.isRequired,
};

ToolPanel.defaultProps = {
  neverShowReminder: false,
};

const mapDispatchToProps = (dispatch) => {
  return {
    onSetNeverShowReminder: () => {
      dispatch(setNeverShowReminder());
    },
  };
  };

const mapStateToProps = ({ attributeCollection, results }) => {
  const labels = {
    saveAndClose: attributeCollection.saveAndClose,
    toolPanelClassificationsText: attributeCollection.toolPanelClassificationsText,
    toolPanelAccommodationText: attributeCollection.toolPanelAccommodationText,
    toolPanelTraitsText: attributeCollection.toolPanelTraitsText,
    toolPanelPaxText: attributeCollection.toolPanelPaxText ,
    toolPanelCalendarText: attributeCollection.toolPanelCalendarText,
  }
  return {
    labels,
    neverShowReminder: results.neverShowReminder,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ToolPanel);
